import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Modal,
    ModalBody,
    Input,
    Label,
    FormGroup,
    Form,
    FormFeedback,
} from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import { AsyncClientSelect, AsyncCandidateSelect } from "components/Common/AsyncSearchSelects";
import { fetchUserOptionsForSelect } from "helpers/asyncFilterSelectApi";
import {
    applyAsyncFilterOptionToFormState,
    applyClientAsyncFilterOptionToFormState,
    buildAsyncFilterInitialOption,
    buildClientAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { addTaskPlanner } from "model/TaskPlannerModel";

const PAGE_NAME = "InternalTaskPlanner";
const clientLabelFieldKey = getClientAsyncFilterLabelFieldKey();
const EXCLUDED_BUCKET_LABEL = "new applicants";
const TASK_TITLE_CANDIDATE_SEPARATOR = " | ";

const stripCandidateSuffixFromTaskTitle = (title = "") => {
    const trimmed = String(title || "").trim();
    const separatorIndex = trimmed.lastIndexOf(TASK_TITLE_CANDIDATE_SEPARATOR);
    if (separatorIndex === -1) return trimmed;
    return trimmed.slice(0, separatorIndex).trim();
};

const buildTaskTitleWithCandidate = (title = "", candidateName = "") => {
    const baseTitle = stripCandidateSuffixFromTaskTitle(title);
    const normalizedCandidateName = String(candidateName || "").trim();
    if (!normalizedCandidateName) return baseTitle;
    if (!baseTitle) return normalizedCandidateName;
    return `${baseTitle}${TASK_TITLE_CANDIDATE_SEPARATOR}${normalizedCandidateName}`;
};

const initialFormData = {
    title: "",
    description: "",
    assignedToUsers: [],
    clientId: "",
    [clientLabelFieldKey]: "",
    candidateClientId: "",
    candidateClientIdLabel: "",
    priority: "high",
    taskBucketId: "",
    status: "not started",
};

const reactSelectPortalStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const InternalTaskPlannerAddModal = ({
    internalTaskPlannerAddModalOpen,
    setInternalTaskPlannerAddModalOpen,
    bucketOptions = [],
    priorityOptions = [],
    statusOptions = [],
    taskBucketGroupId = "",
    assignedToUserSelectContext = { pageName: PAGE_NAME },
    onTaskAdded,
}) => {
    const [formData, setFormData] = useState({ ...initialFormData });
    const [formKey, setFormKey] = useState(0);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [assignedToUserOptions, setAssignedToUserOptions] = useState([]);
    const [assignedToUserOptionsLoading, setAssignedToUserOptionsLoading] = useState(false);

    const normalizedBucketOptions = useMemo(
        () =>
            (Array.isArray(bucketOptions) ? bucketOptions : [])
                .map((option) => ({
                    value: String(option?.value ?? option?.id ?? "").trim(),
                    label: String(option?.label ?? option?.title ?? "").trim(),
                }))
                .filter(
                    (option) =>
                        option.value &&
                        option.label.trim().toLowerCase() !== EXCLUDED_BUCKET_LABEL
                ),
        [bucketOptions]
    );

    const normalizedPriorityOptions = useMemo(
        () =>
            (Array.isArray(priorityOptions) ? priorityOptions : [])
                .map((option) => ({
                    value: String(option?.value ?? "").trim(),
                    label: String(option?.label ?? option?.value ?? "").trim(),
                }))
                .filter((option) => option.value),
        [priorityOptions]
    );

    const normalizedStatusOptions = useMemo(
        () =>
            (Array.isArray(statusOptions) ? statusOptions : [])
                .map((option) => ({
                    value: String(option?.value ?? "").trim(),
                    label: String(option?.label ?? option?.value ?? "").trim(),
                }))
                .filter((option) => option.value),
        [statusOptions]
    );

    const closeModal = () => {
        setInternalTaskPlannerAddModalOpen(false);
        setErrors({});
    };

    const resetForm = () => {
        setFormData({ ...initialFormData });
        setFormKey((previous) => previous + 1);
        setErrors({});
    };

    useEffect(() => {
        if (!internalTaskPlannerAddModalOpen) return;
        resetForm();
    }, [internalTaskPlannerAddModalOpen]);

    useEffect(() => {
        if (!internalTaskPlannerAddModalOpen) {
            setAssignedToUserOptions([]);
            return;
        }

        let cancelled = false;
        setAssignedToUserOptionsLoading(true);

        fetchUserOptionsForSelect("", {
            pageName: assignedToUserSelectContext?.pageName || PAGE_NAME,
        })
            .then((options) => {
                if (!cancelled) {
                    setAssignedToUserOptions(Array.isArray(options) ? options : []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setAssignedToUserOptions([]);
                    toast.error("Unable to load users");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setAssignedToUserOptionsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [internalTaskPlannerAddModalOpen, assignedToUserSelectContext?.pageName]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((previous) => ({ ...previous, [name]: value }));
        if (errors[name]) {
            setErrors((previous) => ({ ...previous, [name]: "" }));
        }
    };

    const validateForm = () => {
        const nextErrors = {};

        if (!String(formData?.title || "").trim()) {
            nextErrors.title = "Title is required.";
        }
        if (!String(formData?.clientId || "").trim()) {
            nextErrors.clientId = "Client is required.";
        }
        if (!String(formData?.candidateClientId || "").trim()) {
            nextErrors.candidateClientId = "Candidate is required.";
        }
        const assignedToUsers = Array.isArray(formData?.assignedToUsers)
            ? formData.assignedToUsers
            : [];
        if (
            !assignedToUsers.some((option) => String(option?.value ?? "").trim())
        ) {
            nextErrors.assignedToUsers = "Assigned To is required.";
        }
        if (!String(formData?.taskBucketId || "").trim()) {
            nextErrors.taskBucketId = "Bucket is required.";
        }
        if (!String(formData?.priority || "").trim()) {
            nextErrors.priority = "Priority is required.";
        }
        if (!String(formData?.status || "").trim()) {
            nextErrors.status = "Status is required.";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validateForm()) return;

        const assignedTo = (Array.isArray(formData?.assignedToUsers) ? formData.assignedToUsers : [])
            .map((option) => String(option?.value ?? "").trim())
            .filter(Boolean);

        const payload = {
            id: "",
            title: String(formData?.title || "").trim(),
            description: String(formData?.description || "").trim(),
            assignedTo,
            candidateClientId: String(formData?.candidateClientId || "").trim(),
            priority: String(formData?.priority || "").trim(),
            taskBucketId: String(formData?.taskBucketId || "").trim(),
            status: String(formData?.status || "").trim(),
            taskBucketGroupId,
        };

        setSaving(true);
        try {
            const response = await addTaskPlanner(payload);
            if (response?.success || response?.isSuccess) {
                toast.success(response?.message || "Task created successfully");
                closeModal();
                onTaskAdded?.();
                return;
            }
            toast.error(response?.message || "Unable to create task");
        } finally {
            setSaving(false);
        }
    };

    const getSelectedOption = (options, value) =>
        options.find((option) => String(option.value) === String(value || "")) || null;

    return (
        <Modal
            isOpen={internalTaskPlannerAddModalOpen}
            toggle={closeModal}
            backdrop="static"
            className="modal-full-height"
            centered
        >
            <ModalBody
                key={formKey}
                className="p-4"
                style={{ maxHeight: "95vh", overflowY: "auto" }}
            >
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Create Task</h5>
                    <Button close onClick={closeModal} />
                </div>
                <hr />

                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label>
                            Title <span className="text-danger">*</span>
                        </Label>
                        <Input
                            type="text"
                            name="title"
                            value={formData.title}
                            invalid={!!errors.title}
                            onChange={handleChange}
                        />
                        <FormFeedback>{errors.title}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>
                            Bucket <span className="text-danger">*</span>
                        </Label>
                        <Select
                            options={normalizedBucketOptions}
                            className={`select2-selection${errors.taskBucketId ? " is-invalid" : ""}`}
                            placeholder="Select Bucket"
                            value={getSelectedOption(normalizedBucketOptions, formData.taskBucketId)}
                            onChange={(selectedOption) => {
                                setFormData((previous) => ({
                                    ...previous,
                                    taskBucketId: selectedOption?.value
                                        ? String(selectedOption.value)
                                        : "",
                                }));
                                if (errors.taskBucketId) {
                                    setErrors((previous) => ({ ...previous, taskBucketId: "" }));
                                }
                            }}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                            styles={reactSelectPortalStyles}
                        />
                        {errors.taskBucketId ? (
                            <FormFeedback className="d-block">{errors.taskBucketId}</FormFeedback>
                        ) : null}
                    </FormGroup>
                    <FormGroup>
                        <Label>
                            Client <span className="text-danger">*</span>
                        </Label>
                        <AsyncClientSelect
                            key={`internal-task-planner-add-client-${formData.clientId}`}
                            pageName={PAGE_NAME}
                            value={formData.clientId}
                            className={`select2-selection${errors.clientId ? " is-invalid" : ""}`}
                            initialOption={buildClientAsyncFilterInitialOption(
                                formData.clientId,
                                formData[clientLabelFieldKey]
                            )}
                            onChange={(option) => {
                                setFormData((previous) => {
                                    const next = applyClientAsyncFilterOptionToFormState(
                                        previous,
                                        option
                                    );
                                    const prevClientId = String(previous?.clientId ?? "").trim();
                                    const nextClientId = String(next?.clientId ?? "").trim();
                                    if (prevClientId !== nextClientId) {
                                        return {
                                            ...next,
                                            candidateClientId: "",
                                            candidateClientIdLabel: "",
                                            assignedToUsers: [],
                                        };
                                    }
                                    return next;
                                });
                                if (errors.clientId) {
                                    setErrors((previous) => ({ ...previous, clientId: "" }));
                                }
                            }}
                            placeholder="Select Client"
                        />
                        {errors.clientId ? (
                            <FormFeedback className="d-block">{errors.clientId}</FormFeedback>
                        ) : null}
                    </FormGroup>

                    <FormGroup>
                        <Label>
                            Candidate <span className="text-danger">*</span>
                        </Label>
                        <AsyncCandidateSelect
                            key={`internal-task-planner-add-candidate-${formData.clientId}-${formData.candidateClientId}`}
                            value={formData.candidateClientId}
                            clientId={formData.clientId}
                            minInputLength={0}
                            emptyHint="Type to search candidates"
                            className={`select2-selection${errors.candidateClientId ? " is-invalid" : ""}`}
                            initialOption={buildAsyncFilterInitialOption(
                                "candidateClientId",
                                formData.candidateClientId,
                                formData
                            )}
                            onChange={(option) => {
                                setFormData((previous) => {
                                    const withCandidate = applyAsyncFilterOptionToFormState(
                                        previous,
                                        "candidateClientId",
                                        option
                                    );
                                    if (!option?.value) {
                                        return {
                                            ...withCandidate,
                                            title: stripCandidateSuffixFromTaskTitle(previous.title),
                                        };
                                    }
                                    const candidateName = String(option?.label || "").trim();
                                    return {
                                        ...withCandidate,
                                        title: buildTaskTitleWithCandidate(
                                            previous.title,
                                            candidateName
                                        ),
                                    };
                                });
                                if (errors.candidateClientId) {
                                    setErrors((previous) => ({
                                        ...previous,
                                        candidateClientId: "",
                                    }));
                                }
                            }}
                            placeholder="Select Candidate"
                        />
                        {errors.candidateClientId ? (
                            <FormFeedback className="d-block">{errors.candidateClientId}</FormFeedback>
                        ) : null}
                    </FormGroup>

                    <FormGroup>
                        <Label>Description</Label>
                        <Input
                            type="textarea"
                            name="description"
                            rows={3}
                            value={formData.description}
                            onChange={handleChange}
                        />
                    </FormGroup>
                   

                    <FormGroup>
                        <Label>
                            Assigned To <span className="text-danger">*</span>
                        </Label>
                        <Select
                            key={`internal-task-planner-add-users-${formKey}-${formData.clientId}`}
                            isMulti
                            options={assignedToUserOptions}
                            value={formData.assignedToUsers}
                            isLoading={assignedToUserOptionsLoading}
                            className={`select2-selection${errors.assignedToUsers ? " is-invalid" : ""}`}
                            classNamePrefix="select2"
                            placeholder="Select Users"
                            onChange={(selectedOptions) => {
                                setFormData((previous) => ({
                                    ...previous,
                                    assignedToUsers: Array.isArray(selectedOptions)
                                        ? selectedOptions
                                        : [],
                                }));
                                if (errors.assignedToUsers) {
                                    setErrors((previous) => ({
                                        ...previous,
                                        assignedToUsers: "",
                                    }));
                                }
                            }}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                            styles={reactSelectPortalStyles}
                        />
                        {errors.assignedToUsers ? (
                            <FormFeedback className="d-block">{errors.assignedToUsers}</FormFeedback>
                        ) : null}
                    </FormGroup>

                    <div className="text-end mt-3">
                        <Button color="primary" type="submit" disabled={saving}>
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default InternalTaskPlannerAddModal;
